/**
 * Rhythmz — Scene: Crystalline Spire
 * ──────────────────────────────────
 * A towering faceted crystal obelisk (stacked dodecahedra) with orbiting
 * torus rings and floating shards. 3-point lighting for specular highlights.
 *
 * Audio: Bass → spire pulse + ring orbit speed. Beat → head flash + shard burst.
 */

import * as THREE from 'three/webgpu';
import { Fn, vec4, float, fract, dot, add, sub, mul, pow, positionLocal, normalLocal, cameraPosition, positionWorld, mix, abs, normalize } from 'three/tsl';
import { SceneLighting, type AudioRef } from './SceneLighting';
import type { SceneHandle } from './SceneManager';

type N = any;

export interface SpireUniforms {
  uTime: N; uBass: N; uMid: N; uBeat: N; uTreble: N;
  uCol0: N; uCol1: N; uCol2: N;
}

export function createCrystallineSpire(u: SpireUniforms): SceneHandle {
  const group = new THREE.Group();
  const lighting = new SceneLighting({
    keyColor: new THREE.Color(0.4, 0.6, 1.0),
    keyIntensity: 15, keyPosition: new THREE.Vector3(5, 8, 5),
    fillColor: new THREE.Color(0.15, 0.2, 0.4),
    fillIntensity: 5, fillPosition: new THREE.Vector3(-4, 0, 6),
    rimColor: new THREE.Color(1.0, 0.5, 0.1),
    rimIntensity: 10, rimPosition: new THREE.Vector3(-3, -2, -6),
    camLightIntensity: 4,
  });
  group.add(lighting.group);

  // ── Shared crystal material ──
  const crystalMat = new THREE.MeshPhysicalNodeMaterial({ metalness: 0.3, roughness: 0.1, clearcoat: 0.5 });
  crystalMat.emissiveNode = Fn(() => {
    const colT = fract(add(mul(u.uTime, float(0.08)), mul(positionLocal.y, float(0.2))));
    const pal = mix(vec4(0.9, 0.15, 0.55, 1), vec4(0.05, 0.45, 0.75, 1), colT);
    const rim = sub(1, abs(dot(normalLocal, normalize(cameraPosition.sub(positionWorld)))));
    return mul(pal, pow(rim, 3)).mul(1.2);
  })();

  // ── Stacked dodecahedron body (taller, bigger) ──
  const bodyParts: THREE.Mesh[] = [];
  const bodyCount = 6;
  const bodyGeo = new THREE.DodecahedronGeometry(0.6, 1);
  for (let i = 0; i < bodyCount; i++) {
    const yPos = -1.8 + i * 0.7;
    const s = 0.8 + (1 - i / bodyCount) * 0.5;
    const mesh = new THREE.Mesh(bodyGeo.clone(), crystalMat);
    mesh.position.set(0, yPos, 0);
    mesh.scale.setScalar(s);
    group.add(mesh);
    bodyParts.push(mesh);
  }

  // ── Head: large octahedron ──
  const headGeo = new THREE.OctahedronGeometry(0.7, 1);
  const headMat = new THREE.MeshPhysicalNodeMaterial({ metalness: 0.3, roughness: 0.1, clearcoat: 0.5 });
  headMat.emissiveNode = Fn(() => {
    const flash = add(0.3, mul(u.uBeat, 2.5));
    return vec4(0.9, 0.2, 0.6, 1).mul(flash);
  })();
  const head = new THREE.Mesh(headGeo, headMat);
  head.position.set(0, 2.2, 0);
  head.scale.setScalar(1.2);
  group.add(head);

  // ── Orbiting torus rings (3 rings) ──
  const ringMat = new THREE.MeshPhysicalNodeMaterial({ metalness: 0.5, roughness: 0.15, clearcoat: 0.3 });
  ringMat.emissiveNode = Fn(() => {
    const colT = fract(mul(u.uTime, 0.15));
    const pal = mix(vec4(0.1, 0.6, 0.9, 1), vec4(1.0, 0.4, 0.0, 1), colT);
    return mul(pal, add(0.2, mul(u.uBass, 0.8)));
  })();
  const rings: THREE.Mesh[] = [];
  for (let i = 0; i < 3; i++) {
    const geo = new THREE.TorusGeometry(1.5 + i * 0.4, 0.05, 12, 64);
    const mesh = new THREE.Mesh(geo, ringMat);
    mesh.rotation.x = 0.3 + i * 0.2;
    mesh.rotation.z = i * 0.5;
    group.add(mesh);
    rings.push(mesh);
  }

  // ── Floating shard instances ──
  const shardCount = 20;
  const shardGeo = new THREE.IcosahedronGeometry(0.12, 2);
  const shards = new THREE.InstancedMesh(shardGeo, crystalMat, shardCount);
  shards.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  group.add(shards);
  const shardData = Array.from({ length: shardCount }, () => ({
    radius: 1.5 + Math.random() * 2,
    speed: 0.2 + Math.random() * 0.6,
    angle: Math.random() * 6.28,
    yOff: -1.5 + Math.random() * 3,
  }));

  let t = 0;
  const pivot = new THREE.Object3D();

  const update = (dt: number): void => {
    t += dt;
    const audio: AudioRef = { bass: u.uBass.value ?? 0, beat: u.uBeat.value ?? 0, mid: u.uMid.value ?? 0, level: ((u.uBass.value ?? 0)+(u.uMid.value ?? 0))*0.5 };
    lighting.update(audio);
    const bass = u.uBass.value ?? 0;

    // Spire rotation
    group.rotation.y = t * 0.15;

    // Body pulse
    bodyParts.forEach((part, i) => {
      const pulse = 1 + bass * 0.12 * (1 - i / bodyCount);
      const s = (0.8 + (1 - i / bodyCount) * 0.5) * pulse;
      part.scale.setScalar(s);
      part.rotation.set(t * 0.1 + i * 0.3, t * 0.15 + i * 0.2, 0);
    });

    // Head flash + rotation
    head.rotation.x = t * 0.4;
    head.rotation.y = t * 0.6;
    head.scale.setScalar(1.2 * (1 + (u.uBeat.value ?? 0) * 0.4));

    // Rings orbit
    rings.forEach((r, i) => {
      r.rotation.x += dt * (0.3 + i * 0.2 + bass * 0.5);
      r.rotation.y += dt * (0.5 + i * 0.3);
      const s = 1 + bass * 0.15;
      r.scale.setScalar(s);
    });

    // Shards orbit
    for (let i = 0; i < shardCount; i++) {
      const d = shardData[i];
      const a = d.angle + t * d.speed;
      const r = d.radius + bass * 0.3;
      pivot.position.set(Math.cos(a) * r, d.yOff + Math.sin(t * 0.5 + i) * 0.3, Math.sin(a) * r);
      pivot.rotation.set(t * d.speed, t * d.speed * 1.3, 0);
      pivot.scale.setScalar(1 + Math.sin(t * 2 + i) * 0.3);
      pivot.updateMatrix();
      shards.setMatrixAt(i, pivot.matrix);
    }
    shards.instanceMatrix.needsUpdate = true;
  };

  const dispose = (): void => {
    lighting.dispose();
    bodyParts.forEach(p => p.geometry.dispose());
    headGeo.dispose(); headMat.dispose();
    rings.forEach(r => r.geometry.dispose());
    ringMat.dispose(); crystalMat.dispose(); shardGeo.dispose();
    group.clear();
  };

  return { group, update, dispose, name: 'Crystalline Spire' };
}